import { SearchSelect } from "@/components/ui/search-select";
import {
	REVENUE_PROVIDER_CONFIG,
	REVENUE_PROVIDER_IDS,
	type RevenueProviderId,
} from "@/lib/revenue-provider-config";

const REVENUE_PROVIDER_OPTIONS = REVENUE_PROVIDER_IDS.map(
	(provider) => REVENUE_PROVIDER_CONFIG[provider].name,
);

const REVENUE_PROVIDER_BY_LABEL = Object.fromEntries(
	REVENUE_PROVIDER_IDS.map((provider) => [
		REVENUE_PROVIDER_CONFIG[provider].name,
		provider,
	]),
) as Record<string, RevenueProviderId>;

// Transparent monochrome marks that need a light tile for contrast.
const PROVIDER_LOGOS: Partial<Record<RevenueProviderId, string>> = {
	polar: "/provider-logos/polar.svg",
};

// Full-bleed brand marks that already include their own background; render
// edge-to-edge inside the badge instead of centered on a white tile.
const FULL_BLEED_PROVIDER_LOGOS: Partial<Record<RevenueProviderId, string>> = {
	dodopayments: "/provider-logos/dodopayments.jpeg",
	lemonsqueezy: "/provider-logos/lemonsqueezy.jpeg",
	revenuecat: "/provider-logos/revenuecat.svg",
	whop: "/provider-logos/whop.jpeg",
};

export function RevenueProviderLogo({
	provider,
}: {
	provider: RevenueProviderId;
}) {
	const logoSrc = PROVIDER_LOGOS[provider];
	if (logoSrc) {
		return (
			<span
				aria-hidden="true"
				className="inline-flex size-5 shrink-0 items-center justify-center rounded-[4px] border border-border bg-white"
			>
				<img
					src={logoSrc}
					alt=""
					className="size-3.5 object-contain"
					loading="lazy"
				/>
			</span>
		);
	}

	const fullBleedLogoSrc = FULL_BLEED_PROVIDER_LOGOS[provider];
	if (fullBleedLogoSrc) {
		return (
			<span
				aria-hidden="true"
				className="inline-flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-[4px]"
			>
				<img
					src={fullBleedLogoSrc}
					alt=""
					className="size-full object-cover"
					loading="lazy"
				/>
			</span>
		);
	}

	if (provider === "stripe") {
		return (
			<span
				aria-hidden="true"
				className="inline-flex size-5 shrink-0 items-center justify-center rounded-[4px] bg-[#635BFF] text-white"
			>
				<svg
					aria-hidden="true"
					focusable="false"
					viewBox="54 67 64 92"
					className="size-3.5"
					fill="currentColor"
				>
					<path d="M79.3,94.7c0-3.9,3.2-5.4,8.5-5.4c7.6,0,17.2,2.3,24.8,6.4V72.2c-8.3-3.3-16.5-4.6-24.8-4.6C67.5,67.6,54,78.2,54,95.9c0,27.6,38,23.2,38,35.1c0,4.6-4,6.1-9.6,6.1c-8.3,0-18.9-3.4-27.3-8v23.8c9.3,4,18.7,5.7,27.3,5.7c20.8,0,35.1-10.3,35.1-28.2C117.4,100.6,79.3,105.9,79.3,94.7z" />
				</svg>
			</span>
		);
	}

	if (provider === "paddle") {
		return (
			<span
				aria-hidden="true"
				className="inline-flex size-5 shrink-0 items-center justify-center rounded-[4px] bg-[#FDDD35] text-[#0E1414]"
			>
				<svg
					aria-hidden="true"
					focusable="false"
					viewBox="-2 9 39 44"
					className="size-3.5"
					fill="currentColor"
				>
					<path d="M34.5152 25.1598C34.5152 33.3314 27.8894 39.9649 19.6964 40.0167H13.5662V50.9999H8.24242V35.1734H19.6964C25.2045 35.1218 29.6582 30.6573 29.6582 25.1598C29.6582 19.6624 25.2045 15.1978 19.6964 15.1461H8.24242V10.303H19.6964C27.8894 10.3545 34.5152 16.9883 34.5152 25.1598Z" />
					<path d="M8.49991 17C8.51217 21.6945 12.3128 25.5001 17 25.5001C12.3128 25.5001 8.51217 29.3055 8.49991 34C8.48783 29.3055 4.68717 25.5001 0 25.5001C4.68717 25.5001 8.48783 21.6945 8.49991 17Z" />
				</svg>
			</span>
		);
	}

	return (
		<span
			aria-hidden="true"
			className="inline-flex size-5 shrink-0 items-center justify-center rounded-[4px] bg-[#151617] text-white"
		>
			<svg
				aria-hidden="true"
				focusable="false"
				viewBox="-2 0 103 100"
				className="size-3.5"
				fill="currentColor"
			>
				<path d="M11.1102 0C13.1187 0.000102309 14.9669 1.09825 15.9281 2.86193L40.2059 47.4106C41.5699 49.9134 44.7048 50.8368 47.2077 49.473C49.7108 48.109 50.6342 44.9742 49.2701 42.4712L30.5466 8.11303C28.554 4.45665 31.2004 0 35.3645 0L92.8064 0C96.8854 0 99.5389 4.29328 97.7147 7.94163L54.0579 95.2544C52.0356 99.298 46.2654 99.298 44.2431 95.2544L0.586298 7.94163C-1.23788 4.29328 1.41563 0 5.49461 0L11.1102 0Z" />
			</svg>
		</span>
	);
}

export function RevenueProviderSelect({
	value,
	onChange,
}: {
	value: RevenueProviderId;
	onChange: (provider: RevenueProviderId) => void;
}) {
	return (
		<SearchSelect
			value={REVENUE_PROVIDER_CONFIG[value].name}
			onChange={(label) => {
				const provider = REVENUE_PROVIDER_BY_LABEL[label];
				if (provider) onChange(provider);
			}}
			options={REVENUE_PROVIDER_OPTIONS}
			placeholder="Select provider"
			searchPlaceholder="Search provider..."
			leadingVisual={(label) => {
				const provider = REVENUE_PROVIDER_BY_LABEL[label];
				return provider ? <RevenueProviderLogo provider={provider} /> : null;
			}}
			clearable={false}
		/>
	);
}
